import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { DeleteHandler, MoveHandler, NodeRendererProps, Tree, TreeApi } from "react-arborist";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  Boxes,
  ChartArea,
  EllipsisVertical,
  Folder,
  FolderOpen,
  Pen,
  Route,
  StarOff,
  Trash2,
} from "lucide-react";
import { useQuery } from "./AppLayout";
import { navigateTo } from "./Navigator";
import { saveFavoriteTree } from "./api/ProjectApi";
import ProjectAction from "./actions/ProjectAction";
import LayoutAction from "./actions/LayoutAction";
import { getJirItemUrl } from "./api/api";
import useResizeObserver from "./useResizeObserver";
import TriangleAlertIcon from "./icons/TriangleAlertIcon";
import ConfirmDialog from "./dialog/ConfirmDialog";
import OctagonAlertIcon from "./icons/OctagonAlertIcon";

import DealsSVG from "./icons/svg/deals.svg?react";
import PeopleSVG from "./icons/svg/people.svg?react";
import CompanySVG from "./icons/svg/companies.svg?react";
import { BorderedRecordIcon } from "./icons/BorderedRecordIcon";
import { getIconByView } from "./main/component/ViewMode";

const DealsNavIcon = (props: any) => (
  <BorderedRecordIcon Icon={DealsSVG} border={false} {...props} />
);
const UsersNavIcon = (props: any) => (
  <BorderedRecordIcon Icon={PeopleSVG} border={false} {...props} />
);
const BuildingNavIcon = (props: any) => (
  <BorderedRecordIcon Icon={CompanySVG} border={false} {...props} />
);
const ProductsNavIcon = (props: any) => <Boxes strokeWidth={1.5} {...props} />;
const ReportsNavIcon = (props: any) => <ChartArea strokeWidth={1.5} {...props} />;
const RouteNavIcon = (props: any) => <Route strokeWidth={1.5} {...props} />;

export type FavoriteTreeNodeType =
  | "Folder"
  | "SavedFilter"
  | "ItemLink"
  | "Dashboard"
  | "Automation";

export type FavoriteTreeNodeTypeNotFolder = Exclude<FavoriteTreeNodeType, "Folder">;

export type FavoriteTreeNode = {
  id: string;
  name: string;
  nodeType: FavoriteTreeNodeType;
  filterId?: string;
  dashboardId?: string;
  automationId?: string;
  itemId?: string;
  itemType?: string;
  iconUrl?: string;
  children?: FavoriteTreeNode[];
};

export const keepFavoriteSavedFilterByIds = (tree = [], idsToKeep) => {
  const idSet = new Set(idsToKeep);
  function helper(list) {
    return list
      .map((node) => {
        if (node.nodeType !== "SavedFilter") {
          return node;
        }
        let children = [];
        if (node.children && node.children.length > 0) {
          children = helper(node.children);
        }
        // keep node if:
        // 1. its id is in list
        // OR
        // 2. it has children that should be kept (to preserve tree structure)
        if (idSet.has(node.filterId) || children.length > 0) {
          return {
            ...node,
            children: children.length > 0 ? children : undefined,
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  return helper(tree);
};

const getAllFavoriteFolder = (tree = []) => {
  let result = [];
  for (const node of tree) {
    if (node.nodeType === "Folder") {
      result.push(node);
    }
    if (node.children?.length) {
      result = result.concat(getAllFavoriteFolder(node.children));
    }
  }
  return result;
};

const removeFavoriteById = (tree = [], idToRemove) => {
  return tree
    .filter((node) => node.id !== idToRemove)
    .map((node) => ({
      ...node,
      children: node.children?.length ? removeFavoriteById(node.children, idToRemove) : [],
    }));
};

type NodeProps = NodeRendererProps<FavoriteTreeNode> & {
  favoriteTree: any;
  myFilters: any;
};

const Node = ({ node, tree, style, dragHandle, favoriteTree, myFilters }: NodeProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query: any = useQuery();
  const { projectId, itemId, dashboardId, ruleId }: any = useParams();

  const [existedFolderName, setExistedFolderName] = useState(false);
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const isCurrent = useMemo(() => {
    if (node.data.nodeType === "SavedFilter") {
      const myFilter =
        myFilters.find((filter) => filter.get("id") === query.get("id")) || fromJS({});
      return node.data.name === myFilter.get("name");
    } else if (node.data.nodeType === "ItemLink") {
      return node.data.itemId == itemId;
    } else if (node.data.nodeType === "Dashboard") {
      return node.data.dashboardId == dashboardId;
    } else if (node.data.nodeType === "Automation") {
      return node.data.automationId == ruleId;
    }
    return false;
  }, [node, itemId, dashboardId, ruleId, myFilters, query]);

  const itemIcon = useMemo(() => {
    const renderIconByContentType = (contentType) => {
      if (contentType === "SALE") {
        return (
          <DealsNavIcon
            className={`h-4 w-4 shrink-0 ${isCurrent ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"}`}
            size={16}
            aria-hidden="true"
          />
        );
      } else if (contentType === "COMPANY") {
        return (
          <BuildingNavIcon
            className={`h-4 w-4 shrink-0 ${isCurrent ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"}`}
            size={16}
            aria-hidden="true"
          />
        );
      } else if (contentType === "CONTACT") {
        return (
          <UsersNavIcon
            className={`h-4 w-4 shrink-0 ${isCurrent ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"}`}
            size={16}
            aria-hidden="true"
          />
        );
      } else if (contentType === "PRODUCT") {
        return (
          <ProductsNavIcon
            className={`h-4 w-4 shrink-0 ${isCurrent ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"}`}
            size={16}
            aria-hidden="true"
          />
        );
      }
      return null;
    };
    if (node.data.nodeType === "SavedFilter") {
      const myFilter =
        myFilters.find((filter) => filter.get("id") === node.data.filterId) || fromJS({});
      const view = myFilter.getIn(["data", "view"]);
      return getIconByView(view);
    } else if (node.data.nodeType === "ItemLink") {
      if (node.data.iconUrl) {
        return (
          <img
            src={node.data.iconUrl}
            className="w-4 h-4 rounded-sm object-cover shrink-0"
            alt=""
          />
        );
      }
      return renderIconByContentType(node.data.itemType);
    } else if (node.data.nodeType === "Dashboard") {
      return (
        <ReportsNavIcon
          className={`h-4 w-4 shrink-0 ${isCurrent ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"}`}
          size={16}
          aria-hidden="true"
        />
      );
    } else if (node.data.nodeType === "Automation") {
      return (
        <RouteNavIcon
          className={`h-4 w-4 shrink-0 ${isCurrent ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600"}`}
          size={16}
          aria-hidden="true"
        />
      );
    }
    return null;
  }, [isCurrent, myFilters, node]);

  const navItemUrl = useMemo(() => {
    if (node.data.nodeType === "SavedFilter") {
      const myFilter =
        myFilters.find((filter) => filter.get("id") === node.data.filterId) || fromJS({});
      const myFilterTypes = myFilter.getIn(["data", "types"])?.toJS() || [];
      const contentType = myFilterTypes.length > 0 ? myFilterTypes[0] : null;
      if (contentType === "SALE") {
        return `/workspaces/${projectId}?id=${myFilter.get("id")}&view=${myFilter.getIn(["data", "view"])}&type=SALE&key=Deals`;
      } else if (contentType === "COMPANY") {
        return `/workspaces/${projectId}?id=${myFilter.get("id")}&view=${myFilter.getIn(["data", "view"])}&type=COMPANY&key=Accounts`;
      } else if (contentType === "CONTACT") {
        return `/workspaces/${projectId}?id=${myFilter.get("id")}&view=${myFilter.getIn(["data", "view"])}&type=CONTACT&key=Contacts`;
      } else if (contentType === "PRODUCT") {
        return `/workspaces/${projectId}?id=${myFilter.get("id")}&view=${myFilter.getIn(["data", "view"])}&type=PRODUCT&key=Products`;
      }
    } else if (node.data.nodeType === "ItemLink") {
      const itemId = node.data.itemId;
      const contentType = node.data.itemType;
      if (contentType === "SALE") {
        return `/workspaces/${projectId}/items/${itemId}?type=SALE`;
      } else if (contentType === "COMPANY") {
        return `/workspaces/${projectId}/items/${itemId}?type=COMPANY`;
      } else if (contentType === "CONTACT") {
        return `/workspaces/${projectId}/items/${itemId}?type=CONTACT`;
      } else if (contentType === "PRODUCT") {
        return `/workspaces/${projectId}/items/${itemId}?type=PRODUCT`;
      }
    } else if (node.data.nodeType === "Dashboard") {
      return `/workspaces/${projectId}/reports/dashboard/${node.data.dashboardId}?key=Reports`;
    } else if (node.data.nodeType === "Automation") {
      return `/workspaces/${projectId}/automations/${node.data.automationId}?key=Automations`;
    }
    return `/workspaces/${projectId}`;
  }, [node, myFilters, projectId]);

  const jiraUrl = useMemo(() => {
    if (node.data.nodeType === "SavedFilter") {
      const myFilter =
        myFilters.find((filter) => filter.get("id") === node.data.filterId) || fromJS({});
      const myFilterTypes = myFilter.getIn(["data", "types"])?.toJS() || [];
      const contentType = myFilterTypes.length > 0 ? myFilterTypes[0] : null;
      if (contentType === "SALE") {
        return getJirItemUrl({ workspaceId: projectId, key: "Deals" });
      } else if (contentType === "COMPANY") {
        return getJirItemUrl({
          workspaceId: projectId,
          view: "GRID",
          type: "COMPANY",
          key: "Accounts",
        });
      } else if (contentType === "CONTACT") {
        return getJirItemUrl({
          workspaceId: projectId,
          view: "GRID",
          type: "CONTACT",
          key: "Contacts",
        });
      } else if (contentType === "PRODUCT") {
        return getJirItemUrl({
          workspaceId: projectId,
          view: "GRID",
          type: "PRODUCT",
          key: "Products",
        });
      }
    } else if (node.data.nodeType === "ItemLink") {
      const itemId = node.data.itemId;
      const contentType = node.data.itemType;
      if (contentType === "SALE") {
        return getJirItemUrl({ workspaceId: projectId, itemId: itemId, key: "Deals" });
      } else if (contentType === "COMPANY") {
        return getJirItemUrl({
          workspaceId: projectId,
          itemId: itemId,
          view: "GRID",
          type: "COMPANY",
          key: "Accounts",
        });
      } else if (contentType === "CONTACT") {
        return getJirItemUrl({
          workspaceId: projectId,
          itemId: itemId,
          view: "GRID",
          type: "CONTACT",
          key: "Contacts",
        });
      } else if (contentType === "PRODUCT") {
        return getJirItemUrl({
          workspaceId: projectId,
          itemId: itemId,
          view: "GRID",
          type: "PRODUCT",
          key: "Products",
        });
      }
    } else if (node.data.nodeType === "Dashboard") {
      return getJirItemUrl({
        workspaceId: projectId,
        view: "CHART",
        key: "Reports",
        suffix: "reports",
      });
    } else if (node.data.nodeType === "Automation") {
      return getJirItemUrl({
        workspaceId: projectId,
        view: "GRID",
        type: "SALE",
        suffix: "automations",
        key: "Automations",
      });
    }
    return getJirItemUrl({ workspaceId: projectId, key: "Deals" });
  }, [node, myFilters, projectId]);

  const handleSubmitConfirm = async () => {
    if (showConfirm.type === "REMOVE_FOLDER") {
      const updatedFavoriteTree = removeFavoriteById(favoriteTree.toJS(), node.data.id);
      await saveFavoriteTree(projectId, updatedFavoriteTree);
      await tree.delete(node);
      dispatch(
        LayoutAction.setNotification({
          title: t("Remove folder"),
          type: "success",
          message: t("Remove folder successfully!"),
        })
      );
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleSaveFolder = async (folderName: string) => {
    if (!folderName || !folderName.trim() || node.data.name === folderName.trim()) {
      setExistedFolderName(false);
      node.reset();
      return;
    }
    const allFolder = getAllFavoriteFolder(favoriteTree.toJS());
    const existedFolders = allFolder.filter(
      (folder) =>
        folder.id !== node.data.id && folder.name.toLowerCase() === folderName.trim().toLowerCase()
    );
    if (existedFolders.length > 0) {
      setExistedFolderName(true);
      return;
    }
    const renameNode = (nodes: FavoriteTreeNode[]): FavoriteTreeNode[] =>
      nodes.map((n) =>
        n.id === node.data.id
          ? { ...n, name: folderName.trim() }
          : { ...n, children: n.children ? renameNode(n.children) : undefined }
      );
    const updatedFavoriteTree = renameNode(favoriteTree.toJS());
    await saveFavoriteTree(projectId, updatedFavoriteTree);
    dispatch(ProjectAction.updateFavoriteTree(updatedFavoriteTree));
    dispatch(
      LayoutAction.setNotification({
        title: t("Folder"),
        type: "success",
        message: t("Update folder successfully"),
      })
    );
    setExistedFolderName(false);
    node.reset();
  };

  const renderNode = () => {
    const isFolder = node.data.nodeType === "Folder";
    if (isFolder) {
      return (
        <div className="group flex items-center justify-between gap-1 p-1 rounded-md hover:bg-gray-100">
          {node.isEditing ? (
            <div>
              <div className="w-full flex items-center gap-x-2">
                <div className="w-4">
                  <Folder className="h-4 w-4 text-gray-600" />
                </div>
                <input
                  data-testid="appnav-folder-input"
                  autoFocus
                  defaultValue={node.data.name}
                  onBlur={(e) => {
                    handleSaveFolder(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSaveFolder(e.currentTarget.value);
                    }
                  }}
                  placeholder={t("Folder name")}
                  className={`w-full px-3 py-0.5 border border-gray-200 
                      rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 
                      focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 
                      disabled:bg-gray-50 disabled:text-gray-400`}
                />
              </div>
              {existedFolderName && (
                <div className="pt-2 text-xs text-red-600">
                  {t("The folder name already exists")}
                </div>
              )}
            </div>
          ) : (
            <div className="w-full flex items-center gap-1 cursor-pointer">
              <div title={node.data.name} className="w-full flex items-center gap-x-2">
                {node.isOpen ? (
                  <FolderOpen className="h-4 w-4 text-gray-600 group-hover:text-blue-600" />
                ) : (
                  <Folder className="h-4 w-4 text-gray-600 group-hover:text-blue-600" />
                )}
                <span className="font-medium text-gray-700 group-hover:text-blue-600">
                  {node.data.name}
                </span>
              </div>
              <div>
                {node.data.children?.length === 0 && (
                  <span className="text-[10px] text-gray-400">{t("Empty")}</span>
                )}
              </div>
            </div>
          )}
          <div>
            <Popover>
              <PopoverButton className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer p-1 outline-none">
                <EllipsisVertical className="h-4 w-4 text-gray-600 hover:text-blue-600" />
              </PopoverButton>
              <PopoverPanel anchor="right start" className="[--anchor-gap:12px] z-20">
                {({ close }) => (
                  <div className="w-max p-1 rounded-md bg-white border border-gray-300">
                    <button
                      className="w-full px-2 py-1 flex items-center gap-2 text-sm rounded-md hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        node.edit();
                        close();
                      }}
                    >
                      <Pen className="w-4 h-4 text-gray-600" />
                      <span>{t("Rename")}</span>
                    </button>
                    <div className="w-full my-1 border-b border-gray-200"></div>
                    <button
                      className="w-full px-2 py-1 flex items-center gap-2 text-red-500 text-sm rounded-md hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowConfirm({
                          show: true,
                          title: t("Remove folder"),
                          description: t("Are you sure you want to remove this folder?"),
                          alertIcon: <TriangleAlertIcon />,
                          dangerBtn: true,
                          type: "REMOVE_FOLDER",
                          data: node.data.id,
                        });
                        close();
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                      <span>{t("Delete folder")}</span>
                    </button>
                  </div>
                )}
              </PopoverPanel>
            </Popover>
            <ConfirmDialog
              visible={showConfirm.show}
              title={showConfirm.title}
              description={showConfirm.description}
              alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
              dangerBtn={showConfirm.dangerBtn}
              onSubmit={handleSubmitConfirm}
              onClose={() => {
                setShowConfirm({ show: false, title: "", description: "" });
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="group flex items-center justify-between gap-1 p-1 rounded-md hover:bg-gray-100">
        <Link
          to={navItemUrl}
          onClick={() => {
            navigateTo({
              jiraUrlParams: {
                relativeUrl: jiraUrl,
              },
              appUrl: navItemUrl,
              navigate,
              forceUseNavigate:
                node.data.nodeType === "SavedFilter" ||
                node.data.nodeType === "Dashboard" ||
                node.data.nodeType === "Automation",
            });
          }}
          title={node.data.name}
          className={`w-full flex items-center gap-x-2 truncate cursor-pointer ${isCurrent ? "bg-gray-100 text-blue-600" : "text-gray-700 hover:text-blue-600"}`}
        >
          {itemIcon}
          <span className="font-medium truncate">{node.data.name}</span>
        </Link>
        <div>
          <div
            onClick={async () => {
              const updatedFavoriteTree = removeFavoriteById(favoriteTree.toJS(), node.data.id);
              await saveFavoriteTree(projectId, updatedFavoriteTree);
              await tree.delete(node);
              dispatch(
                LayoutAction.setNotification({
                  title: t("Favorites"),
                  type: "success",
                  message: t("Remove from favorites successfully"),
                })
              );
            }}
            title={t("Remove favorite")}
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          >
            <StarOff className="h-4 w-4 text-gray-600 hover:text-blue-600" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={dragHandle}
      style={{
        ...style,
      }}
      onClick={() => node.toggle()}
    >
      {renderNode()}
    </div>
  );
};

const AppNavFavoriteTree = ({ favoriteTree, isAddFolder, setIsAddFolder }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();

  const [containerRef, size]: any = useResizeObserver();
  const rowHeight = 32;

  const myFilters = useSelector((state: any) => state.filterReducer.get("myFilters")) || fromJS([]);

  const [height, setHeight] = useState(0);
  const [existedFolderName, setExistedFolderName] = useState(false);

  const treeRef = useRef<TreeApi<any>>(null);

  useEffect(() => {
    updateHeight();
  }, [favoriteTree]);

  const updateHeight = () => {
    requestAnimationFrame(() => {
      const visible = treeRef.current?.visibleNodes.length ?? 0;
      setHeight(visible * rowHeight);
    });
  };

  const handleMove: MoveHandler<FavoriteTreeNode> = async ({ dragIds, parentId, index }) => {
    const dragged = [];
    let sourceParentId = null;
    let sourceIndex = -1;

    function remove(nodes, parent = null) {
      return nodes.filter((node, i) => {
        if (dragIds.includes(node.id)) {
          dragged.push(node);
          sourceParentId = parent;
          sourceIndex = i;
          return false;
        }
        if (node.children) {
          node.children = remove(node.children, node.id);
        }
        return true;
      });
    }

    function insert(nodes) {
      if (parentId === null) {
        if (sourceParentId === null && sourceIndex < index) {
          index -= dragged.length;
        }
        nodes.splice(index, 0, ...dragged);
        return nodes;
      }
      for (const node of nodes) {
        if (node.id === parentId) {
          node.children = node.children || [];
          if (sourceParentId === parentId && sourceIndex < index) {
            index -= dragged.length;
          }
          node.children.splice(index, 0, ...dragged);
        } else if (node.children) {
          insert(node.children);
        }
      }
      return nodes;
    }

    const newTree = remove([...favoriteTree.toJS()]);
    const movedTree = insert(newTree);
    await saveFavoriteTree(projectId, movedTree);
    dispatch(ProjectAction.updateFavoriteTree(movedTree));
  };

  const handleDelete: DeleteHandler<FavoriteTreeNode> = ({ ids }) => {
    const removeNodes = (nodes: FavoriteTreeNode[]): FavoriteTreeNode[] =>
      nodes
        .filter((n) => !ids.includes(n.id))
        .map((n) => ({
          ...n,
          children: n.children ? removeNodes(n.children) : undefined,
        }));
    dispatch(ProjectAction.updateFavoriteTree(removeNodes(favoriteTree.toJS())));
  };

  const handleSaveFolder = async (folderName: string) => {
    if (!folderName || !folderName.trim()) {
      setExistedFolderName(false);
      setIsAddFolder(false);
      return;
    }
    const allFolder = getAllFavoriteFolder(favoriteTree.toJS());
    const existedFolders = allFolder.filter(
      (folder) => folder.name.toLowerCase() === folderName.trim().toLowerCase()
    );
    if (existedFolders.length > 0) {
      setExistedFolderName(true);
      return;
    }
    const updatedFavoriteTree = [
      {
        id: `Folder-${Date.now()}`,
        name: folderName.trim(),
        nodeType: "Folder",
        children: [],
      },
      ...favoriteTree.toJS(),
    ];
    await saveFavoriteTree(projectId, updatedFavoriteTree);
    dispatch(ProjectAction.updateFavoriteTree(updatedFavoriteTree));
    dispatch(
      LayoutAction.setNotification({
        title: t("Folder"),
        type: "success",
        message: t("Add new folder successfully"),
      })
    );
    setExistedFolderName(false);
    setIsAddFolder(false);
  };

  return (
    <>
      {isAddFolder && (
        <div className="pl-4">
          <div className="w-full h-[32px] flex items-center gap-x-2 px-1">
            <div className="w-4">
              <Folder className="h-4 w-4 text-gray-600" />
            </div>
            <input
              data-testid="appnav-folder-input"
              autoFocus
              onBlur={(e) => {
                handleSaveFolder(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveFolder(e.currentTarget.value);
                }
              }}
              placeholder={t("Folder name")}
              className={`w-full px-3 py-0.5 border border-gray-200 
                      rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 
                      focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 
                      disabled:bg-gray-50 disabled:text-gray-400`}
            />
          </div>
          {existedFolderName && (
            <div className="pt-2 text-xs text-red-600">{t("The folder name already exists")}</div>
          )}
        </div>
      )}
      <div ref={containerRef} className="pl-4 w-full h-full">
        <Tree
          ref={treeRef}
          data={favoriteTree.toJS()}
          // openByDefault={false}
          onMove={handleMove}
          onDelete={handleDelete}
          onToggle={updateHeight}
          width={size.width}
          height={height}
          className="overflow-hidden!"
          rowHeight={rowHeight}
          rowClassName="outline-none"
          disableDrop={({ dragNodes, parentNode }) => {
            if (!parentNode) return false;
            if (parentNode.level === -1) return false;
            const dragType = dragNodes[0].data.nodeType;
            const parentType = parentNode.data.nodeType;
            const rules = {
              Folder: ["Folder", "SavedFilter", "ItemLink", "Dashboard", "Automation"],
              SavedFilter: [],
              ItemLink: [],
              Dashboard: [],
              Automation: [],
            };
            return !rules[parentType]?.includes(dragType);
          }}
        >
          {(props) => <Node {...props} favoriteTree={favoriteTree} myFilters={myFilters} />}
        </Tree>
      </div>
    </>
  );
};

export default AppNavFavoriteTree;
